import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';
import { getRedirectPath } from '../../auth/routes';
import { cancelLoginPairingSession } from './loginPairingQrApi';
import { normalizeLoginRedirect } from './login.redirects';
import { audioAlerts } from '../../utils/audioAlerts';
import type { LoginPairingPayload } from './loginPairingQrApi';

const LOGIN_PAIRING_BLOCKING_STATUSES = new Set(['CLAIMED', 'VERIFIED']);

interface UseLoginPairingProps {
  saveLocalAccountShortcut: (userData: any, status?: 'active' | 'pending_security') => boolean;
  setError: (error: string) => void;
  setSuccessMessage: (message: string) => void;
}

export function useLoginPairing({
  saveLocalAccountShortcut,
  setError,
  setSuccessMessage,
}: UseLoginPairingProps) {
  const [loginPairingBlockingMessage, setLoginPairingBlockingMessage] = useState('');
  const [loginPairingSession, setLoginPairingSession] = useState<{
    sessionId: string;
    status: string;
    expiraEm?: string;
    maxExpiraEm?: string | null;
  } | null>(null);
  const [loginPairingUserPreview, setLoginPairingUserPreview] = useState<LoginPairingPayload['user'] | null>(null);
  const [loginPairingSecondsLeft, setLoginPairingSecondsLeft] = useState(0);
  const loginPairingAlertPlayedRef = useRef(false);

  const login = useAuthStore((state) => state.login);
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const loginPairingIsBlocking = Boolean(
    loginPairingSession &&
    LOGIN_PAIRING_BLOCKING_STATUSES.has(loginPairingSession.status),
  );

  const getPendingLoginPairingSessionFromRedirect = useCallback(() => {
    const redirectParam = normalizeLoginRedirect(
      new URLSearchParams(window.location.search).get('redirect'),
    );
    if (!redirectParam) return null;

    try {
      const redirectUrl = new URL(redirectParam, window.location.origin);
      if (!redirectUrl.pathname.startsWith('/security/bonding')) return null;

      const sessionId = redirectUrl.searchParams.get('s')?.trim() || '';
      const intent = redirectUrl.searchParams.get('intent')?.trim() || '';
      if (!sessionId || intent !== 'login_pairing') return null;

      return { sessionId };
    } catch {
      return null;
    }
  }, []);

  const handleLoginPairingSessionChange = useCallback((next: {
    sessionId: string | null;
    status: string;
    expiraEm?: string;
    maxExpiraEm?: string | null;
    user?: LoginPairingPayload['user'] | null;
  } | null) => {
    if (!next?.sessionId) {
      setLoginPairingSession(null);
      setLoginPairingUserPreview(null);
      return;
    }
    if (next.user !== undefined) {
      setLoginPairingUserPreview(next.user);
    }
    setLoginPairingSession({
      sessionId: next.sessionId,
      status: next.status,
      expiraEm: next.expiraEm,
      maxExpiraEm: next.maxExpiraEm,
    });
  }, []);

  useEffect(() => {
    const expiraEm = loginPairingSession?.expiraEm;
    if (!expiraEm) {
      setLoginPairingSecondsLeft(0);
      return;
    }

    const updateCountdown = () => {
      const secondsLeft = Math.max(0, Math.ceil((new Date(expiraEm).getTime() - Date.now()) / 1000));
      setLoginPairingSecondsLeft(secondsLeft);

      if (
        secondsLeft <= 0 &&
        loginPairingSession &&
        LOGIN_PAIRING_BLOCKING_STATUSES.has(loginPairingSession.status)
      ) {
        setLoginPairingSession(null);
        setLoginPairingUserPreview(null);
        setLoginPairingBlockingMessage('');
      }
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [loginPairingSession, loginPairingSession?.expiraEm, loginPairingSession?.status]);

  const cancelLoginPairing = useCallback(async () => {
    if (!loginPairingSession?.sessionId) return;
    try {
      await cancelLoginPairingSession(loginPairingSession.sessionId, getDeviceFingerprint());
    } catch {
      // cancelamento local nao bloqueia o fluxo
    } finally {
      setLoginPairingSession(null);
      setLoginPairingUserPreview(null);
      setLoginPairingBlockingMessage('');
    }
  }, [loginPairingSession?.sessionId]);

  const handleLoginPairingPaired = useCallback((payload: LoginPairingPayload) => {
    const pairedUser = payload.user;
    const pairingLogin = payload.login;
    const shortcutUser = {
      ...pairedUser,
      role: pairedUser.perfil,
      roles: [pairedUser.perfil],
    };

    setLoginPairingUserPreview(pairedUser);

    if (!pairingLogin?.access_token || !pairingLogin.user) {
      saveLocalAccountShortcut(shortcutUser, 'pending_security');
      setLoginPairingBlockingMessage('');
      setSuccessMessage(`Perfil ${pairedUser.nome || pairedUser.email} adicionado neste computador.`);
      return;
    }

    setLoginPairingBlockingMessage('Entrando na conta vinculada pelo celular...');

    try {
      const rawUser = pairingLogin.user as any;
      const perfil = rawUser.perfil || rawUser.role || pairedUser.perfil;
      const userData = {
        ...rawUser,
        perfil,
        role: rawUser.role || perfil,
        roles: Array.isArray(rawUser.roles) && rawUser.roles.length > 0 ? rawUser.roles : [perfil],
      };

      if (pairingLogin.refresh_token) {
        localStorage.setItem('ac_refresh_token', pairingLogin.refresh_token);
      }

      login(pairingLogin.access_token, userData);
      saveLocalAccountShortcut(userData, 'active');
      navigate(getRedirectPath(userData.perfil || '', userData.cargo || undefined), { replace: true });
    } catch {
      setLoginPairingBlockingMessage('');
      saveLocalAccountShortcut(shortcutUser, 'pending_security');
      setError('Não foi possível entrar automaticamente pelo QR. O perfil foi salvo para tentar novamente.');
    }
  }, [login, navigate, saveLocalAccountShortcut, setError, setSuccessMessage]);

  // ------------------------------------------------------------------
  // Cleanup helper for login pairing state reset (Commit A)
  const cleanupLoginPairing = useCallback(() => {
    setLoginPairingSession(null);
    setLoginPairingUserPreview(null);
    setLoginPairingBlockingMessage('');
  }, []);

  useEffect(() => {
    if (!loginPairingSession) return;
    if (!['EXPIRED', 'CANCELLED', 'DONE'].includes(loginPairingSession.status)) return;

    // Deferred state reset using extracted cleanup function
    const timer = setTimeout(() => {
      cleanupLoginPairing();
    }, 0);
    return () => clearTimeout(timer);
  }, [loginPairingSession?.status, cleanupLoginPairing]);

  useEffect(() => {
    if (!loginPairingIsBlocking) {
      loginPairingAlertPlayedRef.current = false;
      return;
    }
    if (loginPairingAlertPlayedRef.current) return;
    loginPairingAlertPlayedRef.current = true;
    void audioAlerts.tocarAlerta('/audio/solicitando_acesso.mp3', { allowDesktop: true });
  }, [loginPairingIsBlocking]);

  useEffect(() => {
    if (isAuthenticated) return;

    const pendingSession = getPendingLoginPairingSessionFromRedirect();
    if (!pendingSession?.sessionId) return;

    let cancelled = false;
    const ping = async () => {
      try {
        await api.post(`/auth/session/${encodeURIComponent(pendingSession.sessionId)}/ping`);
      } catch {
        if (!cancelled) {
          // keepalive best-effort: a tela de login continua o fluxo normal
        }
      }
    };

    void ping();
    const timer = window.setInterval(ping, 10_000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [getPendingLoginPairingSessionFromRedirect, isAuthenticated]);

  return {
    loginPairingSession,
    loginPairingUserPreview,
    loginPairingSecondsLeft,
    loginPairingIsBlocking,
    loginPairingBlockingMessage,
    handleLoginPairingSessionChange,
    handleLoginPairingPaired,
    cancelLoginPairing,
  };
}
